import type { TurnTrace } from './chatTraceStore'

/** A message id and the values it interpolates. */
export interface ActivitySummary {
  id: string
  values: { seconds?: number; count?: number }
}

/** Nothing to show. The activity row renders nothing at all for one of these. */
export function isEmptyTrace(trace: TurnTrace): boolean {
  return !trace.thinking && trace.tools.length === 0
}

/**
 * The one-line label of a collapsed, finished trace.
 *
 * Computed here rather than assembled in JSX so the plural and the rounding are
 * testable: the component suites mock `react-intl` to echo message ids back
 * (the real provider does not resolve messages under `react-scripts test`), so
 * a label built inside the component could not be asserted on at all.
 */
export function activitySummary(trace: TurnTrace): ActivitySummary {
  const seconds = trace.durationMs === undefined ? undefined : Math.round(trace.durationMs / 1000)
  const count = trace.tools.length

  if (seconds !== undefined && count > 0) {
    return { id: 'CHAT.ACTIVITY.SUMMARY_FULL', values: { seconds, count } }
  }
  if (seconds !== undefined) return { id: 'CHAT.ACTIVITY.SUMMARY_TIME', values: { seconds } }
  if (count > 0) return { id: 'CHAT.ACTIVITY.SUMMARY_TOOLS', values: { count } }
  return { id: 'CHAT.ACTIVITY.SUMMARY_PLAIN', values: {} }
}

/**
 * Should the reasoning pane open by itself?
 *
 * The backend guidance is "collapsed by default", which is right once an answer
 * is being written and wrong before it: on many turns the service streams
 * hundreds of `thinking` events and ZERO `answer` events, and a collapsed panel
 * over a 90-second silence is the same blank wait this feature exists to
 * remove. So it opens while there is nothing else to look at, and closes the
 * moment the first answer delta lands.
 *
 * An explicit user toggle overrides this for the rest of the turn; the caller
 * owns that override.
 */
export function thinkingAutoExpanded(input: {
  isLive: boolean
  hasThinking: boolean
  hasAnswer: boolean
}): boolean {
  return input.isLive && input.hasThinking && !input.hasAnswer
}
